import "@arco-design/web-react/es/Modal/style";
import _Modal from "@arco-design/web-react/es/Modal";
import "@arco-design/web-react/es/Space/style";
import _Space from "@arco-design/web-react/es/Space";
import _IconCheck from "@arco-design/web-react/icon/react-icon/IconCheck";
import _IconSave from "@arco-design/web-react/icon/react-icon/IconSave";
import "@arco-design/web-react/es/Button/style";
import _Button from "@arco-design/web-react/es/Button";
import _IconClose from "@arco-design/web-react/icon/react-icon/IconClose";
import "@arco-design/web-react/es/Spin/style";
import _Spin from "@arco-design/web-react/es/Spin";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import "@arco-design/web-react/es/Form/style";
import _Form from "@arco-design/web-react/es/Form";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Decimal from 'decimal.js';
import dayjs from 'dayjs';
import { flowExcelStore, useFlowForm, clearAll, useApproveUsers, initApproveUsers, setFormEvent, useFormEvent } from '@/store/flowExcelStore';
import { clearListSelectorCache } from '../ListSelector';
import { openConfirm } from '@/utils/confirm';
import { FormIcon, ViewIcon } from '../Icons';
import { ProcessTabs } from './ProcessTabs';
import { ApproveForm } from './ApproveForm';
import { ExcelFormBody } from './ExcelFormBody';
import { LeaveForm } from './form';
import { FlowFormContext } from './FlowFormContext';
import { ReturnSelect, returnSelectConfirm } from './ReturnSelect';
import { convertIndexedFormJson, flattenToIndexedFormJson, toApproverPairs } from '@/utils/jsx';
import { validateForms } from '@/utils/formValidation';
import { formulas } from '@/utils/formula';
import './index.less';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/**
 * 统一的审批中心弹窗 props。把原 FormModal / ExcelModal 合并：
 *   - formCode 以 'excel_' 开头 → 走 Excel 分支（动态模板渲染）
 *   - 其他 → 走 Form 分支（静态 <LeaveForm />）
 *
 * 来自 ExcelModal 的额外 prop（nodeId / instanceId）在 Form 分支下被忽略。
 */

// ---------------------------------------------------------------------------
// 常量
// ---------------------------------------------------------------------------

const DEFAULT_FORM_WIDTH = 1200;
const DEFAULT_EXCEL_WIDTH = 1300;
const DEFAULT_TOP = 100;

/** 业务表单类型：Form 走静态 <LeaveForm />，Excel 走动态模板 jsx */

/** 按前缀判断走哪个分支（开放给外部复用，例如 ModalContext 通用 open） */
export function getBizFormKind(formCode) {
  return formCode?.startsWith('excel_') ? 'excel' : 'form';
}

// ---------------------------------------------------------------------------
// 主组件
// ---------------------------------------------------------------------------

function FlowCenterModal({
  visible,
  onCancel,
  onSubmit,
  onApproveSubmit,
  mode,
  type,
  data,
  formCode,
  instanceId,
  loading = false,
  width,
  top = DEFAULT_TOP,
  nodeId
}) {
  // 单 form 实例注册到 flowExcelStore。flowFormStore 已合并进 flowExcelStore，
  // 主表单 / 审批表单都从这一个 store 读。
  const [form] = _Form.useForm();
  const [jsxInit, setJsxInit] = useState(false);
  useFlowForm(form);
  const [formEvent] = useFormEvent();
  const submitHandlerRef = useRef(undefined);
  const userInfo = useSelector(state => state.userInfo);
  const [approveUsers] = useApproveUsers();
  const enumObjectMap = useSelector(state => state.dicts?.enumObjectMap || {});
  const enumObjectData = useSelector(state => state.dicts?.enumObjectData || {});
  const enumListMap = useSelector(state => state.dicts?.enumListMap || {});
  const kind = getBizFormKind(formCode);
  const isAdd = mode === 'add';
  const isView = mode === 'view';
  const isSubmit = type === 'submit';
  const isApproveType = type === 'approve';

  // 退回目标节点用 ref 存：ReturnSelect 的 onConfirm 和 handleApproveSubmit
  // 在 openConfirm 的 onOk 里同步连续调用，useState 来不及更新，
  // 闭包里读到的永远是打开弹窗时的旧值（''），提交的 returnToNodeId 会丢。
  const returnToNodeIdRef = useRef('');

  // 默认宽度按 kind 区分（保持原 FormModal/ExcelModal 各自的默认 1200/1300）
  const modalWidth = width ?? (kind === 'excel' ? DEFAULT_EXCEL_WIDTH : DEFAULT_FORM_WIDTH);
  const processCode = enumObjectData?.modal_process_config?.[formCode]?.data?.processCode || '';
  useEffect(() => {
    // visible 关闭时 clearAll 会清掉 formEvent；重新打开必须重新轮询注入，
    // 否则第二次打开同一个 formCode 时 formEvent 为 undefined，init 永远不会执行。
    if (!visible || !formCode) return;
    let cancelled = false;
    let timer = null;
    function tryInit() {
      if (cancelled) return;
      // formInit 由 loadRuntimeConfig 运行时拉取并注入到 flowExcelStore.runtimeFormInit
      // （fetch + new Function 执行 /js/formInit.js，避免 onChange 闭包被 toString 丢变量）。
      const formInitFn = flowExcelStore.runtimeFormInit;
      if (!formInitFn) return; // 还没加载好，继续轮询
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      const formMap = formInitFn({
        flowExcelStore,
        Decimal,
        formulas,
        dayjs
      });
      const event = formMap[formCode];
      // 存到全局 store，方便 ExcelFormBody / ApproveForm 等子组件订阅
      setFormEvent(event);
    }
    tryInit();
    // 最多等 30s，每 100ms 重试一次（loadRuntimeConfig 完成后写入 flowExcelStore）
    timer = setInterval(tryInit, 100);
    const stop = setTimeout(() => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }, 30000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      clearTimeout(stop);
    };
  }, [formCode, visible]);
  useEffect(() => {
    if (!formEvent || !formCode || !flowExcelStore.approveForm) return;
    if (!isAdd && !data?.id) return;
    if (!jsxInit) return;
    formEvent.init?.(data);
  }, [formCode, data, formEvent, jsxInit, isAdd]);

  // 退回节点 + clearAll 副作用：弹窗关闭时清空 flowExcelStore 的字段映射
  useEffect(() => {
    if (visible) {
      window.React = React;
    }
    if (!visible) {
      // 复位握手标记：下次打开要等 ExcelFormBody 重新渲染出 .excel-table
      // 后再由 tryInit 置回 true，保证 init 在新表格就绪后才执行。
      setJsxInit(false);
      clearAll();
      window.React = void 0;
    }
  }, [visible]);

  // Excel 分支的所有"模板加载 + formProperty 加载 + jsx 编译"已移到
  // ExcelFormBody 内部；本组件不再持有 initLoading / state。

  // 打开时拉审批节点配置：Form 分支固定查 'form_leave'，Excel 分支按 formCode 查
  useEffect(() => {
    if (!visible) return;
    const formLeave = enumListMap.flow_form_users?.find(item => item.value === formCode);
    const {
      children = []
    } = formLeave || {};
    initApproveUsers(children);
    // 仅依赖 visible / formCode / kind：open 时拉一次审批节点配置；data 变化不重拉。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, formCode, kind]);

  // 弹窗打开时滚到顶
  useEffect(() => {
    if (!visible) return;
    const id = setTimeout(() => {
      const container = document.querySelector('.lx-task-modal .arco-modal-content');
      if (container) container.scrollTop = 0;
    }, 0);
    return () => clearTimeout(id);
  }, [visible]);

  // 弹窗显示 / 关闭 / data 变化时的副作用
  // Form 分支把业务数据写进主表单；Excel 分支由 ExcelFormBody 内部处理
  // （模板挂载时自己读取 data 写进 form）。
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      // 审批表单是全局 store 里的 form 实例，不会随 ApproveForm unmount 销毁，
      // 不显式清空的话上次选中的审批人字段会保留到下次开弹框。
      flowExcelStore.approveForm?.resetFields();
      submitHandlerRef.current = undefined;
      clearListSelectorCache();
      returnToNodeIdRef.current = '';
      return;
    }

    // 业务表单切换 / formCode 切换 / 重新打开：清掉上一次的 submitHandler。
    // 不清的话：上次 LeaveForm 注册的 handler 会被 Excel 分支的提交误用，导致
    // values 里没有 userName / name / leaveDays 等 leave 字段，title 渲染成。
    submitHandlerRef.current = undefined;
    if (!isAdd && !data?.id) {
      return;
    }
    if (formCode && visible && kind === 'form') {
      form.setFieldsValue(flattenToIndexedFormJson(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, data, formCode, kind, form]);
  const buttonVisibility = useMemo(() => {
    return {
      // Excel 分支没有暂存按钮
      showDraft: kind === 'form' && (isAdd || isSubmit),
      showAddSubmit: kind === 'excel' ? !data?.id && isAdd || isSubmit // Excel 始终显示提交（包含 add / submit）
      : !data?.id && isAdd || isSubmit,
      showRejectReturn: isApproveType,
      showApprove: isApproveType,
      showComment: isApproveType
    };
  }, [kind, data, isAdd, isSubmit, isApproveType]);
  const {
    showDraft,
    showAddSubmit,
    showRejectReturn,
    showApprove,
    showComment
  } = buttonVisibility;
  function handleAction(config) {
    openConfirm({
      title: config.title,
      content: config.content,
      onOk: config.onOk
    });
  }

  // ----- 提交（新增） -----
  async function handleSubmit() {
    const approveForm = flowExcelStore.approveForm;
    // 同时校验主表单 + 审批表单（如果有）：
    //   - Promise.all 等两者都 settle，再分别判断
    //   - 任一失败：弹窗已经自动定位到错误字段，这里直接 return
    const result = await validateForms([form, approveForm]);
    if (!result.ok) {
      _Message.warning('请检查表单必填项');
      return;
    }
    const [values, approveValues] = result.values;
    handleAction({
      title: '确认提交',
      content: '确定要提交该申请吗？提交后将进入审批流程。',
      onOk: async () => {
        const handler = submitHandlerRef.current;
        const finalValues = handler ? await handler(values) : values;
        let title = values.name;
        if (formCode === 'excel_materials') {
          title = `物资采购申请单_${Date.now()}`;
        }
        onSubmit?.({
          definitionId: processCode,
          title,
          initiatorName: userInfo?.userName || '',
          initiatorId: userInfo.userId || '',
          formData: {
            ...convertIndexedFormJson(finalValues),
            ...(kind === 'excel' ? {
              title,
              formCode,
              processCode
            } : {}),
            ...(kind === 'form' ? {
              formCode,
              processCode
            } : {})
          },
          nextAssignees: toApproverPairs(approveValues)
        });
      }
    });
  }

  // ----- 暂存（仅 Form 分支） -----
  async function handleDraft() {
    const approveForm = flowExcelStore.approveForm;
    const values = form.getFieldsValue();
    const approveValues = approveForm?.getFieldsValue() || {};
    const handler = submitHandlerRef.current;
    const finalValues = handler ? await handler(values) : values;
    onSubmit?.({
      definitionId: processCode,
      title: values.name,
      initiatorName: userInfo?.userName || '',
      initiatorId: userInfo.userId || '',
      draft: true,
      formData: {
        ...finalValues,
        ...approveValues,
        formCode,
        processCode
      },
      nextAssignees: {
        ...approveValues
      }
    });
  }

  // ----- 审批（同意 / 驳回 / 退回） -----
  async function handleApproveSubmit(actionOverride) {
    const approveForm = flowExcelStore.approveForm;
    const isRejectOrReturn = actionOverride === 'reject' || actionOverride === 'return';

    // 驳回 / 退回不校验必填：这两类操作只依赖处理意见（和退回节点），
    // 业务表单/审批人没填完也不应该阻止退回。
    let values;
    let approveValues;
    if (isRejectOrReturn) {
      values = form.getFieldsValue();
      approveValues = approveForm?.getFieldsValue() || {};
    } else {
      const result = await validateForms([form, approveForm]);
      if (!result.ok) {
        _Message.warning('请检查表单必填项');
        return;
      }
      [values, approveValues] = result.values;
    }
    const {
      comment
    } = approveValues;
    const handler = submitHandlerRef.current;
    const finalValues = handler ? await handler(values) : values;
    onApproveSubmit?.({
      formData: {
        ...data,
        ...convertIndexedFormJson(finalValues)
      },
      action: actionOverride || 'approve',
      comment: comment || undefined,
      nextAssignees: toApproverPairs(approveValues),
      userId: userInfo?.userId || '',
      returnToNodeId: actionOverride === 'return' ? returnToNodeIdRef.current || undefined : undefined,
      ...(kind === 'excel' ? {} : {})
    });
  }
  const contextValue = {
    type: mode,
    data,
    setSubmitHandler: handler => {
      submitHandlerRef.current = handler;
    }
  };
  return /*#__PURE__*/React.createElement(_Modal, {
    wrapClassName: `lx-task-modal lx-task-modal-${kind}`,
    maskClosable: false,
    alignCenter: false
    // Excel 分支用 unmountOnExit 清干净 jsx，Form 分支用 unmountOnExit 保持一致行为
    ,
    unmountOnExit: true,
    autoFocus: false,
    focusLock: false,
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      style: {
        color: 'rgb(var(--primary-6)'
      }
    }, isView ? /*#__PURE__*/React.createElement(ViewIcon, {
      style: {
        fontSize: 18
      }
    }) : /*#__PURE__*/React.createElement(FormIcon, {
      style: {
        fontSize: 18
      }
    })), /*#__PURE__*/React.createElement("span", {
      style: {
        marginLeft: 8
      }
    }, enumObjectMap?.modal_process_config?.[formCode] || '')),
    visible: visible,
    style: {
      top,
      width: modalWidth
    },
    onCancel: onCancel,
    footer: kind === 'excel' ? /*#__PURE__*/React.createElement(React.Fragment, null) : null
  }, /*#__PURE__*/React.createElement(FlowFormContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(_Spin, {
    loading: loading,
    style: {
      display: 'block'
    }
  }, kind === 'excel' ?
  /*#__PURE__*/
  // Excel 分支：动态 jsx 模板。ExcelFormBody 内部拉模板 + 编译 jsx。
  React.createElement(ExcelFormBody, {
    form: form,
    formCode: formCode,
    data: data,
    nodeId: nodeId,
    mode: mode,
    isView: isView,
    init: () => {
      setJsxInit(true);
    }
  }) :
  /*#__PURE__*/
  // Form 分支：静态 <LeaveForm />
  React.createElement(_Form, {
    form: form,
    disabled: mode === 'view'
  }, /*#__PURE__*/React.createElement(LeaveForm, null)), !!approveUsers.length && /*#__PURE__*/React.createElement(ApproveForm, {
    type: mode,
    showComment: showComment,
    bizId: data?.id,
    eventId: data?.id ? data?.eventId : 'start',
    processVersion: data?.processVersion
  })), /*#__PURE__*/React.createElement(_Space, {
    className: "lx-task-buttons"
  }, showRejectReturn && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Button, {
    icon: /*#__PURE__*/React.createElement(_IconClose, null),
    status: "danger",
    onClick: () => {
      const approveForm = flowExcelStore.approveForm;
      const comment = approveForm?.getFieldValue('comment');
      if (!comment) {
        _Message.warning('请输入退回意见');
        return;
      }
      handleAction({
        title: '确认驳回',
        content: '确定要驳回该申请吗？',
        onOk: () => handleApproveSubmit('reject')
      });
    }
  }, "\u9A73\u56DE"), /*#__PURE__*/React.createElement(_Button, {
    icon: /*#__PURE__*/React.createElement(_IconClose, null),
    status: "danger",
    onClick: () => {
      const approveForm = flowExcelStore.approveForm;
      const comment = approveForm?.getFieldValue('comment');
      if (!comment) {
        _Message.warning('请输入退回意见');
        return;
      }
      handleAction({
        title: '确认退回',
        content: /*#__PURE__*/React.createElement(ReturnSelect, {
          instanceId: instanceId,
          onConfirm: nodeId => {
            returnToNodeIdRef.current = nodeId;
          }
        }),
        onOk: () => {
          returnSelectConfirm();
          handleApproveSubmit('return');
        }
      });
    }
  }, "\u9000\u56DE")), showDraft && /*#__PURE__*/React.createElement(_Button, {
    icon: /*#__PURE__*/React.createElement(_IconSave, null),
    onClick: () => handleAction({
      title: '确认暂存',
      content: '确定要暂存该申请吗？',
      onOk: handleDraft
    })
  }, "\u6682\u5B58"), showAddSubmit && /*#__PURE__*/React.createElement(_Button, {
    icon: /*#__PURE__*/React.createElement(_IconCheck, null),
    type: "primary",
    onClick: handleSubmit
  }, "\u63D0\u4EA4"), showApprove && /*#__PURE__*/React.createElement(_Button, {
    icon: /*#__PURE__*/React.createElement(_IconCheck, null),
    type: "primary",
    onClick: () => handleAction({
      title: '确认操作',
      content: '确定要执行该操作吗？',
      onOk: () => handleApproveSubmit('approve')
    })
  }, "\u540C\u610F"))), /*#__PURE__*/React.createElement(ProcessTabs, {
    id: data?.id,
    processCode: processCode,
    processVersion: isAdd ? -1 : data?.processVersion
  }));
}
export { FlowCenterModal };